import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft } from 'lucide-react';
import { BRAND } from '../lib/brand';

/**
 * LegalLayout — wrapper condiviso per tutte le policy pages
 * (Privacy, Terms, Cookie Policy, DSAR).
 *
 * ★ 2026-08-27 (Phase 57.B) — Layout comune con:
 *   • header con logo + Back-to-home localizzato
 *   • titolo + intro
 *   • blocco dati Titolare del trattamento
 *   • sezione contenuto (children)
 *   • footer con dati company + version + email legale
 *
 * Il lang prefix (`/it`, `/en`, `/es`) viene letto dal `useParams()` così
 * il link "Back to home" mantiene il funnel corretto.
 */
export function LegalLayout({ title, intro, children, docType = 'privacy' }) {
  const { t } = useTranslation();
  const params = useParams();
  const lang = params.lang || 'it';
  const homeHref = `/${lang}`;

  return (
    <div className="min-h-screen bg-[var(--surface-1)]" data-testid={`legal-${docType}-page`}>
      {/* Header */}
      <header className="px-6 md:px-12 py-6 border-b border-border bg-[var(--surface-2)]/40">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
          <Link to={homeHref} className="flex items-center gap-3" data-testid="legal-home-link">
            <img src={BRAND.greenParadiseLogoSmall} alt="HCF Property" className="h-9 w-auto" />
            <div className="hidden sm:block leading-tight">
              <div className="font-serif text-base text-[hsl(var(--primary))]">HCF Property</div>
              <div className="text-[9px] tracking-[0.25em] uppercase text-muted-foreground">House Cash Flows LTD</div>
            </div>
          </Link>
          <Link
            to={homeHref}
            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            data-testid="legal-back-home"
          >
            <ChevronLeft className="w-3 h-3" /> {t('legal.shared.backHome', 'Torna alla home')}
          </Link>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-3xl mx-auto px-6 md:px-12 py-10 md:py-14">
        <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground" data-testid="legal-eyebrow">
          {t('legal.company.name', 'House Cash Flows LTD')}
        </div>
        <h1 className="font-serif text-3xl md:text-5xl text-[hsl(var(--primary))] mt-2 leading-tight" data-testid="legal-title">
          {title}
        </h1>
        <div className="mt-2 text-xs text-muted-foreground" data-testid="legal-version">
          {t('legal.company.policyVersion', 'Versione 1.0.0 · aggiornata al 27/08/2026')}
        </div>

        {intro && (
          <p className="mt-6 text-sm md:text-base text-foreground/90 leading-relaxed" data-testid="legal-intro">
            {intro}
          </p>
        )}

        {/* Controller box */}
        <aside className="mt-8 p-4 md:p-5 rounded-xl border border-border bg-card" data-testid="legal-controller-box">
          <div className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
            {t('legal.shared.controller', 'Titolare del trattamento')}
          </div>
          <div className="mt-2 text-sm font-medium text-foreground">{t('legal.company.name', 'House Cash Flows LTD')}</div>
          <div className="mt-1 text-xs text-muted-foreground">
            {t('legal.company.address', '')}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {t('legal.company.vat', 'VAT: BG207751044')} · <a href={`mailto:${t('legal.company.email', 'Legal@housecashflows.com')}`} className="underline hover:text-foreground">{t('legal.company.email', 'Legal@housecashflows.com')}</a>
          </div>
        </aside>

        {/* Sections */}
        <div className="mt-10 space-y-6 text-sm md:text-[15px] leading-relaxed text-foreground/90" data-testid="legal-sections">
          {children}
        </div>

        {/* Related links */}
        <div className="mt-14 pt-6 border-t border-border">
          <div className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-3">
            {t('legal.shared.seeAlso', 'Vedi anche')}
          </div>
          <div className="flex flex-wrap gap-3 text-xs">
            {docType !== 'privacy' && (
              <Link to={`/${lang}/privacy`} className="underline hover:text-foreground" data-testid="legal-link-privacy">
                {t('legal.privacy.pageTitle', 'Privacy Policy')}
              </Link>
            )}
            {docType !== 'terms' && (
              <Link to={`/${lang}/terms`} className="underline hover:text-foreground" data-testid="legal-link-terms">
                {t('legal.terms.pageTitle', 'Termini e Condizioni')}
              </Link>
            )}
            {docType !== 'cookiePolicy' && (
              <Link to={`/${lang}/cookie-policy`} className="underline hover:text-foreground" data-testid="legal-link-cookies">
                {t('legal.cookiePolicy.pageTitle', 'Cookie Policy')}
              </Link>
            )}
            {docType !== 'dsar' && (
              <Link to={`/${lang}/dsar`} className="underline hover:text-foreground" data-testid="legal-link-dsar">
                {t('legal.dsar.pageTitle', 'Esercita i tuoi diritti')}
              </Link>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="px-6 md:px-12 py-8 border-t border-border bg-[var(--surface-2)]/50">
        <div className="max-w-4xl mx-auto text-[11px] text-muted-foreground text-center leading-relaxed">
          <div>{t('legal.company.name', 'House Cash Flows LTD')} · {t('legal.company.vat', 'VAT: BG207751044')}</div>
          <div className="mt-0.5">{t('legal.company.address', '')}</div>
          <div className="mt-0.5">
            <a href={`mailto:${t('legal.company.email', 'Legal@housecashflows.com')}`} className="underline hover:text-foreground">
              {t('legal.company.email', 'Legal@housecashflows.com')}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LegalLayout;

/**
 * Section — helper per creare una sezione titolata dentro LegalLayout.
 */
export function LegalSection({ id, title, children }) {
  return (
    <section id={id} data-testid={id ? `legal-section-${id}` : undefined}>
      <h2 className="font-serif text-xl md:text-2xl text-foreground mt-2">{title}</h2>
      <div className="mt-2 text-sm md:text-[15px] leading-relaxed text-foreground/85">{children}</div>
    </section>
  );
}
